// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FindByErpParameters, getProductByErp } from "@bg/App/Services/FindProductByErpService";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

type Parameter = {
    erpNumber: string;
};

type Props = {
    apiParameter: FindByErpParameters;
    product: ProductModel;
    productInfo: ProductInfo;
    pricingLoaded?: boolean;
    skipPricingAndInventory: boolean;
};

type HandlerType = Handler<Parameter, Props>;

export const BeginLoadingProduct: HandlerType = props => {
    props.skipPricingAndInventory = false;
    props.dispatch({
        type: "ShopByLook/BeginLoadingProduct",
        productErp: props.parameter.erpNumber,
    } as any);
};

export const CallProductFromApi: HandlerType = async props => {
    const products = (props.getState() as any).shopByLook.byErp;
    if (!props.parameter.erpNumber) {
        return;
    }
    const doesItExists = Object.keys(products).filter(key => key && key === props.parameter.erpNumber)?.[0];
    if (doesItExists) {
        props.product = products[doesItExists].product;
        props.productInfo = products[doesItExists].productInfo;
        props.skipPricingAndInventory = true;
        return;
    }
    let waiting = false;
    props.apiParameter = { ...props.apiParameter, erpNumbers: props.parameter.erpNumber };
    const result = await getProductByErp(props.apiParameter);
    if (result.successful && result.product) {
        props.product = result.product;
        waiting = true;
    }
    waitFor(() => waiting);
};

export const SetProductData: HandlerType = props => {
    if (props.product) {
        const product = props.product;
        props.dispatch({
            type: "ShopByLook/AddProductData",
            product,
        } as any);
        if (!props.skipPricingAndInventory) {
            props.productInfo = createFromProduct(props.product);
        }
    }
};

export const LoadPricing: HandlerType = async props => {
    if (props.skipPricingAndInventory || !props.parameter.erpNumber) {
        return;
    }

    let pricingLoaded = false;
    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: [props.productInfo],
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    if (!realTimePricingProps.apiResult) {
                        pricingLoaded = true;
                        return;
                    }

                    props.productInfo.pricing = realTimePricingProps.apiResult?.realTimePricingResults?.find(
                        o => o.productId === props.product.id,
                    );

                    pricingLoaded = true;
                } else if (realTimePricingProps.error) {
                    pricingLoaded = true;
                    props.productInfo.failedToLoadPricing = true;
                }
            },
        }),
    );
    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => pricingLoaded);
    }
};

export const LoadInventory: HandlerType = async props => {
    if (props.skipPricingAndInventory || !props.parameter.erpNumber) {
        return;
    }
    const product = props.product;
    if (!product) {
        return;
    }
    props.productInfo = createFromProduct(product);

    let loadedInventory = false;

    props.dispatch(
        loadRealTimeInventory({
            productIds: [product.id],
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps.error) {
                    if (props.productInfo) {
                        props.productInfo.failedToLoadInventory = true;
                    }
                } else if (props.productInfo) {
                    props.productInfo.inventory = realTimeInventoryProps.apiResult?.realTimeInventoryResults?.find(
                        o => o.productId === product.id,
                    );
                }
                loadedInventory = true;
            },
        }),
    );

    await waitFor(() => loadedInventory);
};

export const SetProductInfo: HandlerType = props => {
    if (props.productInfo) {
        const productInfo = props.productInfo;
        props.dispatch({
            type: "ShopByLook/AddProductInfo",
            productInfo,
            productErp: props.parameter.erpNumber,
        } as any);
    }
};

export const DispatchCompleteSetProduct: HandlerType = props => {
    props.dispatch({
        type: "ShopByLook/CompleteLoadingProduct",
        productErp: props.parameter.erpNumber,
    } as any);
};

export const chain = [
    BeginLoadingProduct,
    CallProductFromApi,
    SetProductData,
    LoadPricing,
    LoadInventory,
    SetProductInfo,
    DispatchCompleteSetProduct,
];

const findProductByErp = createHandlerChainRunner(chain, "FindProductByErp");
export default findProductByErp;
